<template>
    <loading-box :loading="loading">
        <h2>Clients</h2>

        <p class="text-red text-sm mt-4" v-if="error" v-html="error"></p>
        <p class="text-green text-sm mt-4" v-else-if="message" v-html="message"></p>

        <table class="table mt-8" v-if="clients.length > 0">
            <thead>
                <tr>
                    <th>Name</th>
                    <th></th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="client in clients">
                    <!-- Name -->
                    <td style="vertical-align: middle;">
                        {{ client.name }}
                    </td>

                    <!-- Upload Button -->
                    <td style="vertical-align: middle;" class="text-center">
                        <a class="text-lg text-purple cursor-pointer" @click="upload(client, 'anniversary')" title="Upload work anniversary">
                            <font-awesome-icon icon="building" />
                        </a>

                        <a class="text-lg text-purple cursor-pointer pl-3" @click="upload(client, 'birthday')" title="Upload birthday">
                            <font-awesome-icon icon="birthday-cake" />
                        </a>

                        <a class="text-lg text-purple cursor-pointer pl-3" @click="remove(client)">
                            <font-awesome-icon icon="user-slash" />
                        </a>
                    </td>
                </tr>
            </tbody>

            <tfoot>
                <tr>
                    <th>Name</th>
                    <th></th>
                </tr>
            </tfoot>
        </table>

        <portal to="modals">
            <transition name="fade">
                <modal v-if="uploadModalOpen" tabindex="-1" role="dialog" id="modal-upload-persons" @modal-close="hideUploadModal">
                    <div
                        class="modal__box  modal__box--large"
                    >
                        <div>
                            <h2 class="modal__ttl">
                                Upload to {{ uploadForm.client.name }}
                                <span class="fal fa-times modal__close" @click="hideUploadModal"></span>
                            </h2>
                            <div class="py-8 px-6">
                                <div v-if="uploadForm.loading">
                                    <loader />
                                </div>
                                <div v-else>
                                    <!-- Create Client Form -->
                                    <form role="form" enctype="multipart/form-data">
                                        <!-- Name -->
                                        <div class="mb-4">
                                            <label class="block text-sm mb-2 text-grey-darkest" for="upload-file">CSV/XLS File Only</label>
                                            <input
                                                class="shadow appearance-none border text-grey-darker leading-tight focus:outline-none focus:shadow-outline h-10 px-4 w-full" style="padding-top: 6px;"
                                                v-bind:class="{'border-red': uploadForm.errors.file}"
                                                id="upload-file"
                                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                                type="file"
                                                ref="file"
                                            />

                                            <p class="text-red text-sm m-0 mt-1" v-if="uploadForm.errors.file" v-html="uploadForm.errors.file"></p>
                                        </div>
                                    </form>

                                    <!-- Form Errors -->
                                    <p class="text-red text-sm mt-8" v-if="uploadForm.errors.general" v-html="uploadForm.errors.general"></p>

                                    <button type="button" class="text-white bg-purple px-6 py-3 rounded hover:bg-purple-dark w-full text-base text-center mt-8" @click="store">Upload</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </modal>

                <modal v-if="removeModalOpen" tabindex="-1" role="dialog" id="modal-remove-person" @modal-close="hideRemoveModal">
                    <div
                        class="modal__box  modal__box--large"
                    >
                        <div>
                            <h2 class="modal__ttl">
                                Remove person from {{ removeForm.client.name }}
                                <span class="fal fa-times modal__close" @click="hideRemoveModal"></span>
                            </h2>
                            <div class="py-8 px-6">
                                <div v-if="removeForm.loading">
                                    <loader />
                                </div>
                                <div v-else>
                                    <!-- Create Client Form -->
                                    <form role="form">
                                        <!-- Name -->
                                        <div class="mb-4">
                                            <label class="block text-sm mb-2 text-grey-darkest" for="upload-email">Email Address</label>
                                            <input
                                                class="shadow appearance-none border text-grey-darker leading-tight focus:outline-none focus:shadow-outline h-10 px-4 w-full" style="padding-top: 6px;"
                                                v-bind:class="{'border-red': removeForm.errors.email}"
                                                id="upload-email"
                                                type="email"
                                                v-model="removeForm.email"
                                            />

                                            <p class="text-red text-sm m-0 mt-1" v-if="removeForm.errors.email" v-html="removeForm.errors.email"></p>
                                        </div>
                                    </form>

                                    <!-- Form Errors -->
                                    <p class="text-red text-sm mt-8" v-if="removeForm.errors.general" v-html="removeForm.errors.general"></p>

                                    <button type="button" class="text-white bg-purple px-6 py-3 rounded hover:bg-purple-dark w-full text-base text-center mt-8" @click="update">Remove</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </modal>
            </transition>
        </portal>
    </loading-box>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import LoadingBox from '@/components/LoadingBox'
    import Modal from '@/components/Modal'
    import Loader from '@/components/Loader'
    import Api from '@/api'
    import Errors from '@/mixins/errors'

    export default {
        mixins: [ Errors ],

        data: () => ({
            loading: true,
            uploadModalOpen: false,
            removeModalOpen: false,

            error: false,
            message: '',

            removeForm: {
                loading: false,
                errors: {},
                client: null,
                email: ''
            },

            uploadForm: {
                loading: false,
                errors: {},
                client: null,
                type: null,
                file: ''
            }
        }),

        mounted() {
            if (this.clients.length == 0) {
                this.fetch().then(() => {
                    this.loading = false;
                });
            } else {
                this.loading = false;
            }
        },

        methods: {
            ...mapActions({ fetch: 'clients/fetch', refetchUpcoming: 'birthdays/fetch', refetchUpcomingAnniversaries: 'anniversaries/fetch' }),

            update() {
                Api.request('post', 'api/clients/remove', {
                    client: this.removeForm.client.id,
                    email: this.removeForm.email
                }).then(({data}) => {
                    if (data.message) {
                        this.message = data.message;
                        this.refetchUpcoming();
                        this.refetchUpcomingAnniversaries();
                    }

                    this.removeForm.loading = false;

                    this.hideRemoveModal();
                }).catch(error => {
                    if (typeof error.response !== 'undefined' && typeof error.response.data !== 'undefined') {
                        this.removeForm.errors = this.setErrors(error.response.data)
                    } else {
                        this.error = 'We were unable to remove a person at this time.';
                        this.hideRemoveModal();
                    }

                    this.removeForm.loading = false;
                });
            },

            remove(client) {
                this.error = '';
                this.message = '';
                this.removeModalOpen = true;
                this.removeForm.client = client;
            },

            hideRemoveModal() {
                this.removeForm.client = null;
                this.removeForm.errors = {};
                this.removeForm.email = '';
                this.removeModalOpen = false;
            },

            store() {
                const formData = new FormData();

                if (typeof this.$refs.file.files[0] === 'undefined') {
                    this.uploadForm.errors = {file: 'The CSV/XLS file is required.'}
                    return;
                }

                this.uploadForm.loading = true;
                this.uploadForm.errors  = {};

                formData.append('file', this.$refs.file.files[0]);
                formData.append('client', this.uploadForm.client.id);
                formData.append('type', this.uploadForm.type);

                Api.request('post', 'api/clients/upload', formData, {
                    headers: {
                        'Content-Type': 'x-www-form-urlencoded'
                    }
                }).then(({data}) => {
                    if (data.message) {
                        this.message = data.message;
                        this.refetchUpcoming();
                        this.refetchUpcomingAnniversaries();
                    }

                    this.uploadForm.loading = false;

                    this.hideUploadModal();
                }).catch(error => {
                    if (typeof error.response !== 'undefined' && typeof error.response.data !== 'undefined') {
                        this.uploadForm.errors = this.setErrors(error.response.data)
                    } else {
                        this.error = 'We were unable to upload your spreadsheet at this time.';
                        this.hideUploadModal();
                    }

                    this.uploadForm.loading = false;
                });
            },

            upload(client, type) {
                this.error = '';
                this.message = '';
                this.uploadModalOpen = true;
                this.uploadForm.client = client;
                this.uploadForm.type = type;
            },

            hideUploadModal() {
                this.uploadForm.client = null;
                this.uploadForm.type = null;
                this.uploadForm.errors = {};
                this.uploadForm.file = '';
                this.uploadModalOpen = false;
            }
        },

        components: {
            'loading-box': LoadingBox,
            'modal': Modal,
            Loader
        },

        computed: {
           ...mapGetters({
              clients: 'clients/items',
           })
        },
    }
</script>
